import type { Load, LoadStop } from '@breaker/shared-types';

/**
 * Assign gate (Mark-X companion, S80). Ready means the paperwork AND the
 * physics are in order before a driver ever sees the job: confirmed status,
 * at least one collection and one delivery, every stop carrying coordinates
 * (geofenced arrivals fire off geog — a stop without a position produces
 * exactly the silent-arrival loads S79 was built to kill) and a time
 * window. First failing check wins; the reason renders under the disabled
 * Assign button. Reassignment of an already-assigned load is deliberately
 * ungated — the gate is about ENTERING assigned.
 */
export type AssignReadiness =
  | { ready: true }
  | { ready: false; reason: string };

function place(stop: LoadStop): string {
  return stop.city ?? stop.postcode ?? stop.line1;
}

export function readyToAssign(load: Load, stops: LoadStop[]): AssignReadiness {
  if (load.status === 'draft') {
    return { ready: false, reason: 'This load is still a draft. Confirm it below, then assign — drivers only ever see confirmed work.' };
  }
  if (load.status !== 'confirmed') {
    return { ready: false, reason: 'Only confirmed loads can be assigned.' };
  }
  if (stops.length === 0) {
    return { ready: false, reason: 'No stops on this load yet.' };
  }
  if (stops.every((s) => s.type !== 'pickup')) {
    return { ready: false, reason: 'Needs a collection stop.' };
  }
  if (stops.every((s) => s.type !== 'dropoff')) {
    return { ready: false, reason: 'Needs a delivery stop.' };
  }
  for (const s of stops) {
    if (s.lat === null || s.lng === null) {
      return {
        ready: false,
        reason: `${place(s)} has no map position — arrivals won't fire. Re-save the stop with a full address.`,
      };
    }
  }
  for (const s of stops) {
    if (s.windowStartAt === null) {
      return { ready: false, reason: `${place(s)} has no time window.` };
    }
  }
  return { ready: true };
}
// EOF-READINESS-V1
